/**
 * Карта маршрутов.
 *
 * Страницы грузятся лениво. Раньше все 39 импортировались сразу, и на
 * первый заход приезжал весь код админки — включая tiptap-редактор,
 * recharts и схемы залов, которые нужны трём экранам из сорока. В dev
 * это ещё заметнее: Vite трансформирует модули по требованию, и
 * первый заход тянул больше тысячи файлов, из них 300+ scss-модулей.
 *
 * `AppShell` и `Login` — исключение: каркас и вход нужны сразу, их
 * ленивость дала бы лишний пустой кадр вместо экономии.
 *
 * Suspense живёт выше, в App: один фолбэк на все переходы.
 */

import {lazy} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';

import {AppShell} from '@pages/AppShell';
import {Login} from '@pages/Login';

const EventCreate = lazy(() =>
  import('@pages/Catalog/EventCreate').then((m) => ({default: m.EventCreate})),
);
const EventDetails = lazy(() =>
  import('@pages/Catalog/EventDetails').then((m) => ({default: m.EventDetails})),
);
const EventEdit = lazy(() =>
  import('@pages/Catalog/EventEdit').then((m) => ({default: m.EventEdit})),
);
const Events = lazy(() => import('@pages/Catalog/Events').then((m) => ({default: m.Events})));
const Regions = lazy(() => import('@pages/Catalog/Regions').then((m) => ({default: m.Regions})));
const SessionCreate = lazy(() =>
  import('@pages/Catalog/SessionCreate').then((m) => ({default: m.SessionCreate})),
);
const SessionDetails = lazy(() =>
  import('@pages/Catalog/SessionDetails').then((m) => ({default: m.SessionDetails})),
);
const TheaterDetail = lazy(() =>
  import('@pages/Catalog/Theaters').then((m) => ({default: m.TheaterDetail})),
);
const Theaters = lazy(() => import('@pages/Catalog/Theaters').then((m) => ({default: m.Theaters})));
const AnnualReports = lazy(() =>
  import('@pages/Finance/AnnualReports').then((m) => ({default: m.AnnualReports})),
);
const PaymentMethods = lazy(() =>
  import('@pages/Finance/PaymentMethods').then((m) => ({default: m.PaymentMethods})),
);
const Reports = lazy(() => import('@pages/Finance/Reports').then((m) => ({default: m.Reports})));
const Tax = lazy(() => import('@pages/Finance/Tax').then((m) => ({default: m.Tax})));
const Home = lazy(() => import('@pages/Home').then((m) => ({default: m.Home})));
const Banners = lazy(() => import('@pages/Marketing/Banners').then((m) => ({default: m.Banners})));
const Content = lazy(() => import('@pages/Marketing/Content').then((m) => ({default: m.Content})));
const Mailing = lazy(() => import('@pages/Marketing/Mailing').then((m) => ({default: m.Mailing})));
const NotFound = lazy(() => import('@pages/NotFound').then((m) => ({default: m.NotFound})));
const Bookings = lazy(() => import('@pages/Sales/Bookings').then((m) => ({default: m.Bookings})));
const CustomerDetails = lazy(() =>
  import('@pages/Sales/Customers').then((m) => ({default: m.CustomerDetails})),
);
const Customers = lazy(() =>
  import('@pages/Sales/Customers').then((m) => ({default: m.Customers})),
);
const Discounts = lazy(() =>
  import('@pages/Sales/Discounts').then((m) => ({default: m.Discounts})),
);
const OrderDetails = lazy(() =>
  import('@pages/Sales/Orders').then((m) => ({default: m.OrderDetails})),
);
const Orders = lazy(() => import('@pages/Sales/Orders').then((m) => ({default: m.Orders})));
const TicketsPrint = lazy(() =>
  import('@pages/Sales/Orders').then((m) => ({default: m.TicketsPrint})),
);
const RefundDetails = lazy(() =>
  import('@pages/Sales/RefundDetails').then((m) => ({default: m.RefundDetails})),
);
const Refunds = lazy(() => import('@pages/Sales/Refunds').then((m) => ({default: m.Refunds})));
const Tickets = lazy(() => import('@pages/Sales/Tickets').then((m) => ({default: m.Tickets})));
const Transplant = lazy(() =>
  import('@pages/Sales/Transplant').then((m) => ({default: m.Transplant})),
);
const Settings = lazy(() => import('@pages/Settings').then((m) => ({default: m.Settings})));
const ApiServices = lazy(() =>
  import('@pages/System/ApiServices').then((m) => ({default: m.ApiServices})),
);
const AppStats = lazy(() => import('@pages/System/AppStats').then((m) => ({default: m.AppStats})));
const SessionSettings = lazy(() =>
  import('@pages/System/SessionSettings').then((m) => ({default: m.SessionSettings})),
);
const SmsCodes = lazy(() => import('@pages/System/SmsCodes').then((m) => ({default: m.SmsCodes})));
const SmsPlatforms = lazy(() =>
  import('@pages/System/SmsPlatforms').then((m) => ({default: m.SmsPlatforms})),
);
const CashierReport = lazy(() =>
  import('@pages/Team/CashierReport').then((m) => ({default: m.CashierReport})),
);
const MyPartners = lazy(() =>
  import('@pages/Team/MyPartners').then((m) => ({default: m.MyPartners})),
);
const Organizations = lazy(() =>
  import('@pages/Team/Organizations').then((m) => ({default: m.Organizations})),
);
const RoleEditor = lazy(() => import('@pages/Team/Roles').then((m) => ({default: m.RoleEditor})));
const RolesList = lazy(() => import('@pages/Team/Roles').then((m) => ({default: m.RolesList})));
const Users = lazy(() => import('@pages/Team/Users').then((m) => ({default: m.Users})));

import {ProtectedRoute, PublicOnlyRoute} from './ProtectedRoute';

export const AppRouter = () => (
  <Routes>
    <Route element={<PublicOnlyRoute />}>
      <Route path="/login" element={<Login />} />
    </Route>

    <Route element={<ProtectedRoute />}>
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />

        <Route path="/sales">
          <Route path="orders" element={<Orders />} />
          <Route path="orders/:orderId" element={<OrderDetails />} />
          <Route path="orders/:orderId/print" element={<TicketsPrint />} />
          <Route path="booked" element={<Bookings />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:customerId" element={<CustomerDetails />} />
          <Route path="tickets" element={<Tickets />} />
          {/* Единая страница возвратов — объединяет легасные
              /refund (источник footer) и /refund-client (profile).
              Старые URL остаются редиректами на сегмент source. */}
          <Route path="refunds" element={<Refunds />} />
          <Route path="refunds/:refundId" element={<RefundDetails />} />
          <Route path="refund" element={<Navigate to="/sales/refunds" replace />} />
          <Route
            path="refund-client"
            element={<Navigate to="/sales/refunds?source=profile" replace />}
          />
          <Route path="discounts" element={<Discounts />} />
          <Route path="transplant" element={<Transplant />} />
        </Route>

        <Route path="/catalog">
          <Route path="events" element={<Events />} />
          <Route path="events/new" element={<EventCreate />} />
          <Route path="events/:id" element={<EventDetails />} />
          <Route path="events/:id/edit" element={<EventEdit />} />
          <Route path="events/:id/sessions/new" element={<SessionCreate />} />
          <Route path="events/:id/sessions/:sessionId" element={<SessionDetails />} />
          <Route path="theaters" element={<Theaters />} />
          <Route path="theaters/:id" element={<TheaterDetail />} />
          <Route path="regions" element={<Regions />} />
        </Route>

        <Route path="/marketing">
          <Route path="banners" element={<Banners />} />
          <Route path="content" element={<Content />} />
          <Route path="mailing" element={<Mailing />} />
        </Route>

        <Route path="/finance">
          <Route path="reports" element={<Reports />} />
          <Route path="annual-reports" element={<AnnualReports />} />
          <Route path="tax" element={<Tax />} />
          <Route path="payment-methods" element={<PaymentMethods />} />
        </Route>

        <Route path="/team">
          <Route path="users" element={<Users />} />
          <Route path="users/roles" element={<RolesList />} />
          <Route path="users/roles/:role" element={<RoleEditor />} />
          <Route path="cashiers/:cashierId" element={<CashierReport />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="my-partners" element={<MyPartners />} />
        </Route>

        <Route path="/system">
          <Route path="sms-codes" element={<SmsCodes />} />
          <Route path="sms-platforms" element={<SmsPlatforms />} />
          <Route path="api-services" element={<ApiServices />} />
          <Route path="app-stats" element={<AppStats />} />
          <Route path="session-settings" element={<SessionSettings />} />
        </Route>

        <Route path="/settings" element={<Settings />} />
      </Route>
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);
